import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAutoTranslation } from "@/entities"
import { ForgotPasswordForm, LoginForm, RegisterForm } from "@/features"
import {
  AUTH_TABS,
  DISCOVERED_TRANSLATION_KEYS,
  LANG_KEYS,
} from "@/shared/constants"
import { SvgLogo } from "@/shared/ui/svg"
import { ArrowLeft, Layers, Headphones, Zap, Eye, Quote } from "lucide-react"
import { useState, memo, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import styles from "./styles.module.scss"

type AuthTab = (typeof AUTH_TABS)[keyof typeof AUTH_TABS]

const getInitialTab = (tabParam: string | null): AuthTab => {
  if (!tabParam) return AUTH_TABS.LOGIN
  if (Object.values(AUTH_TABS).includes(tabParam as AuthTab))
    return tabParam as AuthTab
  return AUTH_TABS.LOGIN
}

// Memoized logo component to prevent re-rendering
const MemoizedLogo = memo(() => (
  <SvgLogo variant="enhanced" className="h-8 w-8" />
))
MemoizedLogo.displayName = "MemoizedLogo"

// Features based on frateca.com
const features = [
  {
    icon: Layers,
    title: DISCOVERED_TRANSLATION_KEYS.MULTITASK_LEARNING,
    description:
      DISCOVERED_TRANSLATION_KEYS.LEARN_WHILE_COMMUTING_EXERCISING_OR_WORKING,
  },
  {
    icon: Eye,
    title: DISCOVERED_TRANSLATION_KEYS.NO_EYE_STRAIN,
    description: DISCOVERED_TRANSLATION_KEYS.SAY_GOODBYE_TO_READING_FATIGUE,
  },
  {
    icon: Headphones,
    title: DISCOVERED_TRANSLATION_KEYS.NATURAL_AI_VOICES,
    description:
      DISCOVERED_TRANSLATION_KEYS.PREMIUM_TEXT_TO_SPEECH_THAT_SOUNDS_HUMAN,
  },
  {
    icon: Zap,
    title: DISCOVERED_TRANSLATION_KEYS.INSTANT_CONVERSION,
    description:
      DISCOVERED_TRANSLATION_KEYS.CONVERT_ANY_TEXT_TO_AUDIO_IN_SECONDS,
  },
]

// Testimonial
const testimonial = {
  quote: DISCOVERED_TRANSLATION_KEYS.TESTIMONIAL_RESEARCH_PAPERS,
  author: "Sarah K.",
  role: DISCOVERED_TRANSLATION_KEYS.GRADUATE_STUDENT,
}

export const AuthPage = () => {
  const { t } = useAutoTranslation()
  const queryParams = useSearchParams()
  const tabParam = queryParams[0]?.get("tab")
  const [activeTab, setActiveTab] = useState<AuthTab>(getInitialTab(tabParam))

  const TAB_DATA = useMemo(() => {
    return {
      [AUTH_TABS.LOGIN]: {
        title: t(LANG_KEYS.WELCOME_BACK),
        description: t(LANG_KEYS.SIGN_IN_TO_CONTINUE),
        content: (
          <LoginForm
            onForgotPassClick={() => setActiveTab(AUTH_TABS.FORGOT_PASSWORD)}
          />
        ),
      },
      [AUTH_TABS.REGISTER]: {
        title: t(LANG_KEYS.CREATE_YOUR_ACCOUNT),
        description: t(LANG_KEYS.START_LISTENING_IN_SECONDS),
        content: <RegisterForm />,
      },
      [AUTH_TABS.FORGOT_PASSWORD]: {
        title: t(LANG_KEYS.RESET_YOUR_PASSWORD),
        description: t(LANG_KEYS.ENTER_EMAIL_FOR_RESET_LINK),
        content: <ForgotPasswordForm />,
      },
    }
  }, [t])

  // Get current tab data
  const currentTabData = TAB_DATA[activeTab]

  return (
    <div className={styles.authPage}>
      {/* Left Panel - Branding (hidden on mobile) */}
      <div className={styles.brandingPanel}>
        <div className={styles.brandingPanel__content}>
          {/* Logo */}
          <div className={styles.brandingPanel__logo}>
            <MemoizedLogo />
            <span className={styles.brandingPanel__logoText}>Frateca</span>
          </div>

          {/* Main headline */}
          <div className={styles.brandingPanel__headline}>
            <h1 className={styles.brandingPanel__title}>
              {t(DISCOVERED_TRANSLATION_KEYS.READ_FASTER)}
              <br />
              <span className={styles.brandingPanel__titleAccent}>
                {t(DISCOVERED_TRANSLATION_KEYS.LEARN_MORE)}
              </span>
            </h1>
            <p className={styles.brandingPanel__subtitle}>
              {t(
                DISCOVERED_TRANSLATION_KEYS.CONVERT_TEXTBOOKS_PAPERS_PDFS_AND_WEB_PAGES_TO_NATURAL_SOUNDING_AUDIO_SAVE_TIME_BY_LISTENING_IN_YOUR_SPARE_MOMENTS
              )}
            </p>
          </div>

          {/* Features grid */}
          <div className={styles.brandingPanel__features}>
            {features.map((feature, index) => (
              <div key={index} className={styles.brandingPanel__feature}>
                <div className={styles.brandingPanel__featureIcon}>
                  <feature.icon className="size-4" />
                </div>
                <div className={styles.brandingPanel__featureText}>
                  <span className={styles.brandingPanel__featureTitle}>
                    {t(feature.title)}
                  </span>
                  <span className={styles.brandingPanel__featureDesc}>
                    {t(feature.description)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div className={styles.brandingPanel__testimonial}>
            <Quote className="text-primary size-5 opacity-60" />
            <p className={styles.brandingPanel__quote}>
              {t(testimonial.quote)}
            </p>
            <div className={styles.brandingPanel__author}>
              <span className={styles.brandingPanel__authorName}>
                {testimonial.author}
              </span>
              <span className={styles.brandingPanel__authorRole}>
                {t(testimonial.role)}
              </span>
            </div>
          </div>

          {/* Social proof - stats from frateca.com */}
          <div className={styles.brandingPanel__social}>
            <div className={styles.brandingPanel__stat}>
              <span className={styles.brandingPanel__statNumber}>450K+</span>
              <span className={styles.brandingPanel__statLabel}>
                {t(DISCOVERED_TRANSLATION_KEYS.MINUTES_GENERATED)}
              </span>
            </div>
            <div className={styles.brandingPanel__statDivider} />
            <div className={styles.brandingPanel__stat}>
              <span className={styles.brandingPanel__statNumber}>150K+</span>
              <span className={styles.brandingPanel__statLabel}>
                {t(DISCOVERED_TRANSLATION_KEYS.HOURS_SAVED)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className={styles.formPanel}>
        <Tabs
          value={activeTab}
          onValueChange={value => {
            setActiveTab(value as AuthTab)
          }}
          className={styles.tabsWrapper}
        >
          {/* Mobile logo */}
          <div className={styles.mobileLogo}>
            <MemoizedLogo />
            <span className={styles.mobileLogo__text}>Frateca</span>
          </div>

          {/* Tab navigation */}
          {activeTab !== AUTH_TABS.FORGOT_PASSWORD && (
            <div className={styles.tabsWrapper__inner}>
              <TabsList className={styles.tabsWrapper__list}>
                <TabsTrigger
                  className={styles.tabsWrapper__trigger}
                  value={AUTH_TABS.LOGIN}
                >
                  {t(LANG_KEYS.LOGIN)}
                </TabsTrigger>
                <TabsTrigger
                  className={styles.tabsWrapper__trigger}
                  value={AUTH_TABS.REGISTER}
                >
                  {t(LANG_KEYS.SIGN_UP)}
                </TabsTrigger>
              </TabsList>
            </div>
          )}

          {/* Card with dynamic content */}
          <Card className={styles.card}>
            <CardHeader className={styles.card__header}>
              {activeTab === AUTH_TABS.FORGOT_PASSWORD && (
                <Button
                  className="absolute top-4 left-4 size-9"
                  variant="ghost"
                  size="icon"
                  onClick={() => setActiveTab(AUTH_TABS.LOGIN)}
                >
                  <ArrowLeft className="size-4" />
                </Button>
              )}
              <CardTitle className={styles.card__title}>
                {currentTabData.title}
              </CardTitle>
              <CardDescription className="text-center">
                {currentTabData.description}
              </CardDescription>
            </CardHeader>

            <CardContent className={styles.card__content}>
              {currentTabData.content}
            </CardContent>
          </Card>
        </Tabs>
      </div>
    </div>
  )
}
